import { useCallback, useEffect, useState } from 'react'
import { RotateCcw, Save } from 'lucide-react'
import { hrApi } from '@/services/api/hr'
import { HRSettingsView } from '@/components/hr-settings/HRSettingsView'
import type { HrScopesResponse, HrSettingsData } from '@/types/hr'
import { extractErrorMessage } from '@/utils/errorHandler'

/**
 * hr2-v2 (E3/E13): HR-Einstellungen im App-Standard — registry-getriebene
 * Karten (HRSettingsView), gepuffertes Dirty-Speichern (ein Roundtrip) statt
 * Sofort-Speichern pro Feld, Selbst-Aktivierungs-Karte einseitig.
 */
export function HRSettingsPage() {
  const [data, setData] = useState<HrSettingsData | null>(null)
  const [scopes, setScopes] = useState<HrScopesResponse | null>(null)
  const [overrides, setOverrides] = useState<Record<string, unknown>>({})
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [busyScope, setBusyScope] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([hrApi.getMySettings(), hrApi.getMyScopes()])
      .then(([settings, scopeResponse]) => {
        setData(settings)
        setScopes(scopeResponse)
      })
      .catch(e => setError(extractErrorMessage(e, { loadError: 'Einstellungen konnten nicht geladen werden' })))
  }, [])

  const updateOverride = useCallback((key: string, value: unknown) => {
    setOverrides(prev => ({ ...prev, [key]: value }))
  }, [])

  const resetKey = useCallback((key: string) => {
    setOverrides(prev => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const save = async () => {
    if (Object.keys(overrides).length === 0) return
    setSaving(true); setError(null); setNotice(null)
    try {
      await hrApi.updateMySettings(overrides)
      const refreshed = await hrApi.getMySettings()
      setData(refreshed)
      setOverrides({})
      setNotice('Einstellungen gespeichert.')
    } catch (e: unknown) {
      setError(extractErrorMessage(e, { loadError: 'Speichern fehlgeschlagen' }))
    } finally { setSaving(false) }
  }

  const discard = () => {
    if (Object.keys(overrides).length > 0 && window.confirm('Alle ungespeicherten Änderungen verwerfen?')) {
      setOverrides({})
    }
  }

  // E13: Selbst-Aktivierung — Zeilen mit erlaubter Selbst-Aktivierung
  // (inkl. bereits aktiver, sonst fehlt das Badge «aktiviert»).
  // OFF→ON = einmaliger Aktivierungsvorgang, danach fix ON + ausgegraut.
  const selfActivate = async (scopeKey: string) => {
    setBusyScope(scopeKey); setError(null); setNotice(null)
    try {
      await hrApi.activateScope(scopeKey)
      const refreshed = await hrApi.getMyScopes()
      setScopes(refreshed)
      setNotice('Modul aktiviert.')
    } catch (e: unknown) {
      setError(extractErrorMessage(e, { loadError: 'Aktivierung fehlgeschlagen' }))
    } finally { setBusyScope(null) }
  }

  const dirty = Object.keys(overrides).length > 0

  const activatableRows = (scopes?.scopes ?? []).filter(s =>
    s.kind === 'module' && s.self_activation && !s.module_active && s.key !== 'page.finance_queue')
  const selfActivationRows = (scopes?.scopes ?? []).filter(s =>
    s.kind === 'module' && s.self_activation && s.key !== 'page.finance_queue')

  return (
    <div className="flex flex-col min-h-full">
      <div className="sticky top-0 z-10 px-4 lg:px-6 py-3 border-b bg-white flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-lg font-bold">Einstellungen</h1>
          <p className="text-xs text-gray-500 max-w-2xl">
            Sichtbare Einstellungen des HR-Moduls. Welche Sie sehen und ändern dürfen, legt der
            Superadmin fest (Scopes, einheitlich für die HR-Rolle).
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <button onClick={discard} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1">
              <RotateCcw className="w-3.5 h-3.5" /> Verwerfen
            </button>
          )}
          <button onClick={save} disabled={saving || !dirty}
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-1.5 disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 px-4 lg:px-6 py-3 text-sm text-red-700">{error}</div>}
      {notice && <div className="bg-green-50 px-4 lg:px-6 py-3 text-sm text-green-700">{notice}</div>}

      <div className="flex-1 min-h-0">
        {data && <HRSettingsView
          data={data}
          editable
          overrides={overrides}
          onUpdateOverride={updateOverride}
          onResetKey={resetKey}
          extraSections={selfActivationRows.length > 0 ? [{
            name: 'Verfügbare Seiten & Funktionen',
            node: (
              <div className="bg-white rounded-lg border p-6 mb-6">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-lg font-semibold text-gray-900">Verfügbare Seiten & Funktionen</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Einige Seiten und Funktionen können Sie einmalig selbst aktivieren. Nach der
                  Aktivierung bleiben sie dauerhaft an — nur der Superadmin kann sie deaktivieren.
                  Lehrerpult und Abrechnungstabelle sind fest und erscheinen hier nicht.
                </p>
                <div className="divide-y divide-gray-100">
                  {selfActivationRows.map(s => {
                    const activating = activatableRows.some(a => a.key === s.key)
                    return (
                      <div key={s.key} className="flex items-center justify-between gap-4 py-3">
                        <div>
                          <div className="text-sm font-medium text-gray-800">{s.label}</div>
                          <div className="text-xs text-gray-500">
                            {activating
                              ? 'Inaktiv — Aktivierung möglich'
                              : 'Dauerhaft aktiviert'}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={!activating || busyScope === s.key}
                            onClick={() => { if (activating) void selfActivate(s.key) }}
                            role="switch"
                            aria-checked={!activating}
                            aria-label={s.label}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                              !activating ? 'bg-indigo-600 opacity-60 cursor-not-allowed' : 'bg-gray-300'
                            } ${busyScope === s.key ? 'opacity-50' : ''}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${!activating ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                          {!activating && (
                            <span className="text-xs text-gray-500 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded">aktiviert</span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ),
          }] : []}
        />}
      </div>
    </div>
  )
}

export default HRSettingsPage
